import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { colors, Grid, IconGraphy, Tab, Badge } from '@leapeasy/ui-kit';
import { Box, Typography } from '@mui/material';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { openDetails } from 'store/actions/uiActions';
import { CurrentInvoiceTable, DisputeHistoryTable } from 'components/InvoiceTable';
import { getInvoiceDetailAction } from 'store/actions/invoiceActions';
import { useDetailRouteTabs } from 'hooks/useDetailRouteTabs';
import { useHydrateDetailsOnce } from 'hooks/useHydrateDetailsOnce';
import { useDemoMask } from 'hooks/useDemoMask';
import { DetailPageShell, SectionPanelSkeleton, TabBarSkeleton } from 'components/DetailPageState';
import { useDetailPageState } from 'hooks/useDetailPageState';
import { useImmutableJs } from 'hooks/useImmutableJs';
import { isInvoicePaid } from 'utils/invoicePaymentStatus';
import { resolveDetailBackTarget } from 'utils/detailBackNavigation';

const INVOICE_TAB_SLUGS = ['current', 'dispute-history'];

export const InvoiceDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { invoiceId } = useParams();

  const basePath = useMemo(() => (invoiceId ? `/invoice/${invoiceId}/detail` : ''), [invoiceId]);
  const { selectedIndex: selected, goToTab } = useDetailRouteTabs({
    basePath,
    tabSlugs: INVOICE_TAB_SLUGS,
  });

  const { mask } = useDemoMask();
  const details = useSelector((state) => state.getIn(['ui', 'details'])) || null;
  const detailsJSON = useImmutableJs(details);
  const invoiceDetail =
    useSelector((state) => state.getIn(['invoice', 'invoiceDetailData'])) || null;
  const invoiceDetailJSON = useImmutableJs(invoiceDetail);
  const invoiceFromApi = invoiceDetailJSON?.data || null;
  const routeMatchesDetails =
    Boolean(detailsJSON?.data && invoiceId) &&
    (`${detailsJSON.data.invoice_id}` === `${invoiceId}` ||
      `${detailsJSON.data.doc_number}` === `${invoiceId}`);
  const hasUiFallback = !invoiceId && Boolean(detailsJSON?.data);
  const {
    data: invoiceDataFromHook,
    isLoading,
    hasError,
  } = useDetailPageState({
    routeId: invoiceId,
    detailRecord: invoiceDetailJSON,
    hasUiFallback,
    fallbackData: detailsJSON?.data || null,
  });
  const invoiceData =
    invoiceDataFromHook || (!isLoading && routeMatchesDetails ? detailsJSON.data : null) || null;

  const onClickBack = useCallback(() => {
    dispatch(openDetails(null));
    const target = resolveDetailBackTarget(location.state, '/invoice');
    if (target.useHistoryBack) {
      navigate(-1);
      return;
    }
    navigate(target.path, target.state ? { state: target.state } : undefined);
  }, [dispatch, navigate, location.state]);

  const onRetry = useCallback(() => {
    if (invoiceId) dispatch(getInvoiceDetailAction(invoiceId));
  }, [invoiceId, dispatch]);

  useEffect(() => {
    if (invoiceId) {
      dispatch(getInvoiceDetailAction(invoiceId));
      return;
    }
    if (!details) {
      onClickBack();
    }
  }, [invoiceId, details, dispatch, onClickBack]);

  useHydrateDetailsOnce({
    type: 'invoice',
    data: invoiceFromApi,
    id: invoiceFromApi?.invoice_id,
  });

  return (
    <DashboardLayoutContainer shouldShowCard={false}>
      {isLoading || hasError ? (
        <>
          <DetailPageShell
            onBack={onClickBack}
            isLoading={isLoading}
            hasError={hasError}
            onRetry={onRetry}
            loadingLabel="Loading invoice details..."
            skeletonVariant="fields"
          />
          {isLoading ? (
            <MainContainer>
              <TabBarSkeleton tabCount={2} />
              <SectionPanelSkeleton />
            </MainContainer>
          ) : null}
        </>
      ) : invoiceData ? (
        <React.Fragment>
          <Box
            padding="24px"
            gap="10px"
            display="flex"
            flexDirection="column"
            border="1px solid #EAEAEA"
            sx={{ background: 'white' }}
            borderRadius="12px"
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Box sx={{ cursor: 'pointer', '&:hover': { opacity: 0.75 } }}>
                <IconGraphy icon="Arrow.ArrowBack" onClick={onClickBack} />
              </Box>
              <Typography
                variant="h3"
                style={{
                  fontSize: '20px',
                  color: colors.purple[900],
                  fontWeight: '700',
                  padding: '5px 5px 5px 15px',
                }}
              >
                {`${mask('invoice-number', invoiceData.doc_number)} - Invoice`}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
                alignItems: 'center',
              }}
            >
              <Badge
                color="magentaRed"
                label={isInvoicePaid(invoiceData.balance) ? 'Paid' : 'Unpaid'}
                rounded
                textSize="medium"
              />
              <Typography variant="body2" color="text.secondary">
                Total: {invoiceData.total_amount ?? '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Balance: {invoiceData.balance ?? '—'}
              </Typography>
            </Box>
          </Box>
          <MainContainer>
            <Grid>
              <Tab
                title="Current Invoice"
                icon="HomePlaces.Villa"
                isSelected={selected === 0}
                onClick={() => goToTab(0)}
              />
              <Tab
                title="Dispute History"
                icon="Chart.ViewKanban"
                isSelected={selected === 1}
                onClick={() => goToTab(1)}
              />
            </Grid>
            {selected === 0 ? (
              <TableWrapper>
                <CurrentInvoiceTable data={invoiceData} />
              </TableWrapper>
            ) : (
              <TableWrapper>
                <DisputeHistoryTable data={invoiceData} />
              </TableWrapper>
            )}
          </MainContainer>
        </React.Fragment>
      ) : (
        <Box padding="24px">
          <Typography variant="body1">
            Invoice not found, or you do not have access to this invoice.
          </Typography>
        </Box>
      )}
    </DashboardLayoutContainer>
  );
};

const MainContainer = styled.div`
  padding: 24px;
  margin-top: 10px;
  flex-direction: column;
  border: 1px solid #eaeaea;
  background: white;
  border-radius: 12px;
`;

const TableWrapper = styled.div`
  margin-top: 20px;
`;
